import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { useAIWorkflow } from "../contexts/AIWorkflowContext";
import OptimizationControls from "../components/dashboard/ai_flow/OptimizationControls";
import PdfEditorComponent from "../components/dashboard/ai_flow/PdfEditorComponent";
import jobService from "../services/jobService";
import { generatePdfBlob } from "../components/dashboard/ai_flow/ResumePdfGenerator";
import { templates } from "../components/dashboard/ai_flow/resumeTemplateStyles";

// Import premium components for PDF generation
import ExecutivePremiumTemplate from "../components/dashboard/ai_flow/PremiumResume/ExecutivePremiumTemplate";
import StaticExecutivePremiumTemplate from "../components/dashboard/ai_flow/PremiumResume/StaticExecutivePremiumTemplate";

// Import CSS as a raw text string for injection
import executivePremiumCss from "../components/dashboard/ai_flow/PremiumResume/ExecutivePremiumTemplate.css?raw";

// Styles
import "../components/dashboard/ai_flow/aiFlowStyles.css";
import "../components/dashboard/widgets/Widgets.css";

// A helper to map component names to the STATIC components for PDF export.
const premiumTemplateComponentsForExport = {
  ExecutivePremiumTemplate: StaticExecutivePremiumTemplate,
};

// A helper to convert structured data to plain text for .txt export
const convertStructuredToText = (data) => {
  if (!data) return "";
  let text = "";

  const htmlToText = (htmlString) => {
    if (!htmlString) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  if (data.header) {
    text += `${htmlToText(data.header.name)}\n${htmlToText(
      data.header.subtitle
    )}\n${htmlToText(data.header.tagline)}\n\n`;
    if (data.header.contactInfo) {
      text += "CONTACT\n";
      text +=
        data.header.contactInfo.map((item) => item.value).join(" | ") + "\n\n";
    }
  }

  data.mainContent?.forEach((section) => {
    text += `${section.title.toUpperCase()}\n\n`;
    if (section.type === "profile") {
      text += `${htmlToText(section.content)}\n\n`;
    } else if (section.type === "experience") {
      section.items.forEach((job) => {
        text += `${htmlToText(job.jobTitle)}\n`;
        text += `${htmlToText(job.companyInfo)}\n`;
        text += `${htmlToText(job.details)}\n\n`;
      });
    }
  });

  data.sidebarContent?.forEach((section) => {
    text += `${section.title.toUpperCase()}\n\n`;
    if (section.type === "education") {
      section.items.forEach((edu) => {
        text += `${htmlToText(edu.degree)}, ${htmlToText(
          edu.institution
        )} (${htmlToText(edu.period)})\n`;
      });
    } else {
      section.items.forEach((item) => {
        const itemText = item.name ? `${item.name} - ${item.period}` : item;
        text += `- ${itemText}\n`;
      });
    }
    text += "\n";
  });
  return text;
};

// A helper to generate simplified, single-column HTML for MS Word export
const generateSimpleHtmlForWord = (data) => {
  if (!data) return "";

  const { header, mainContent, sidebarContent } = data;

  const styles = `
    body { font-family: Calibri, 'Helvetica Neue', Arial, sans-serif; color: #333; }
    h1 { font-size: 24pt; font-weight: bold; color: #2c3e50; margin-bottom: 5px; }
    h2 { font-size: 11pt; font-weight: normal; text-transform: uppercase; letter-spacing: 3px; color: #34495e; margin-top: 0; margin-bottom: 10px; }
    h3 { font-size: 12pt; font-weight: bold; color: #34495e; margin-top: 20px; margin-bottom: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h4 { font-size: 11pt; font-weight: bold; margin-bottom: 2px; color: #2c3e50; }
    p, li { font-size: 10pt; line-height: 1.5; margin-bottom: 4px; }
    ul { padding-left: 20px; }
    a { color: #0000EE; text-decoration: underline; }
    .company-info { color: #555; }
    .institution-info { font-style: italic; color: #666; }
    .tagline { font-style: italic; color: #7f8c8d; }
  `;

  const unwrap = (htmlString) =>
    (htmlString || "").replace(/^<p>/, "").replace(/<\/p>$/, "");
  let body = "";

  if (header) {
    body += `<h1>${unwrap(header.name)}</h1>`;
    body += `<h2>${unwrap(header.subtitle)}</h2>`;
    body += `<p class="tagline">${unwrap(header.tagline)}</p>`;
    if (header.contactInfo) {
      body += `<p>${header.contactInfo.map((c) => c.value).join(" | ")}</p>`;
    }
    body += "<hr />";
  }

  mainContent.forEach((section) => {
    body += `<h3>${section.title}</h3>`;
    if (section.type === "profile") {
      body += section.content;
    } else if (section.type === "experience") {
      section.items.forEach((job) => {
        body += `<h4>${unwrap(job.jobTitle)}</h4>`;
        body += `<p class="company-info">${unwrap(job.companyInfo)}</p>`;
        body += job.details;
      });
    }
  });

  sidebarContent.forEach((section) => {
    body += `<h3>${section.title}</h3>`;
    if (section.type === "education") {
      section.items.forEach((edu) => {
        body += `<h4>${unwrap(edu.degree)}</h4>`;
        body += `<p class="institution-info">${unwrap(
          edu.institution
        )} | ${unwrap(edu.period)}</p>`;
        body += edu.details || "";
      });
    } else if (
      section.type === "competencies" ||
      section.type === "certifications"
    ) {
      const listItems = section.items
        .map(
          (item) =>
            `<li>${item.name ? `${item.name} - ${item.period}` : item}</li>`
        )
        .join("");
      body += `<ul>${listItems}</ul>`;
    }
  });

  return `<html><head><meta charset="UTF-8"><style>${styles}</style></head><body>${body}</body></html>`;
};

function ResumeOptimizationPage() {
  const navigate = useNavigate();
  const {
    selectedJobForOptimization,
    finalResumeContentForJobSearch,
    optimizeResumeAction,
    isLoadingOptimizedResume,
    optimizeResumeError,
    optimizedResume,
    setOptimizedResume,
    aiSessionId,
    incrementResumeListVersion,
    structuredResumeData,
    setStructuredResumeData,
    generatePremiumResumeAction,
    isLoadingPremiumResume,
    setFinalResumeContentForJobSearch,
  } = useAIWorkflow();

  // --- Template State (from ResumeBuilderPage) ---
  const basicTemplates = templates.filter((t) => t.type === "basic");
  const premiumTemplates = templates.filter((t) => t.type === "premium");
  const [activeTemplateType, setActiveTemplateType] = useState("basic");
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    basicTemplates[0]?.id || templates[0].id
  );
  const selectedTemplate =
    templates.find((t) => t.id === selectedTemplateId) || templates[0];

  const [editedOptimizedResume, setEditedOptimizedResume] = useState("");
  const [pagesData, setPagesData] = useState(null); // Track page structure
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [actionStatus, setActionStatus] = useState({
    state: "idle",
    message: "",
  });
  const [isPageReadyForCheck, setIsPageReadyForCheck] = useState(false);
  const [optimizedStructuredData, setOptimizedStructuredData] = useState(null);
  const [isGeneratingPremiumBase, setIsGeneratingPremiumBase] = useState(false);
  const [isTemplateSelectorCollapsed, setIsTemplateSelectorCollapsed] =
    useState(false);
  const [previousJobId, setPreviousJobId] = useState(null);

  // --- Refs for Transitions ---
  const basicDropdownRef = useRef(null);
  const premiumDropdownRef = useRef(null);
  const contentRef = useRef(null);

  const baseResumeAvailable = !!finalResumeContentForJobSearch;

  useEffect(() => {
    if (aiSessionId) {
      setIsPageReadyForCheck(true);
    }
  }, [aiSessionId]);

  useEffect(() => {
    if (isPageReadyForCheck && !isLoadingOptimizedResume) {
      // Check prerequisites
      const hasJob = !!selectedJobForOptimization;
      const hasSession = !!aiSessionId;

      // For base resume, we now allow null or empty string (which triggers profile fallback)
      // Only redirect if we have absolutely no way to generate a resume
      const canGenerateResume =
        baseResumeAvailable ||
        (finalResumeContentForJobSearch === "" && hasSession) ||
        (finalResumeContentForJobSearch === null && hasSession);

      const prerequisitesMet = hasJob && hasSession && canGenerateResume;

      if (!prerequisitesMet) {
        console.log("[ResumeOptimizationPage] Prerequisites not met:", {
          hasJob,
          hasSession,
          canGenerateResume,
          baseResumeAvailable,
          finalResumeContentForJobSearch,
        });
        navigate("/dashboard/job-listing", { replace: true });
      }
    }
  }, [
    selectedJobForOptimization,
    aiSessionId,
    baseResumeAvailable,
    isLoadingOptimizedResume,
    navigate,
    isPageReadyForCheck,
    finalResumeContentForJobSearch,
  ]);

  // Effect to generate premium base resume if needed
  useEffect(() => {
    const generatePremiumIfNeeded = async () => {
      if (
        selectedTemplate.type === "premium" &&
        !structuredResumeData &&
        !isLoadingPremiumResume &&
        !isGeneratingPremiumBase &&
        aiSessionId
      ) {
        setIsGeneratingPremiumBase(true);
        try {
          await generatePremiumResumeAction();
        } catch (error) {
          console.error("Failed to generate premium resume:", error);
        } finally {
          setIsGeneratingPremiumBase(false);
        }
      }
    };

    generatePremiumIfNeeded();
  }, [
    selectedTemplate.type,
    structuredResumeData,
    isLoadingPremiumResume,
    isGeneratingPremiumBase,
    aiSessionId,
    generatePremiumResumeAction,
  ]);

  // Update edited resume when we get a new optimization
  useEffect(() => {
    if (optimizedResume) {
      console.log(
        "[ResumeOptimizationPage] New optimized resume received, updating editor"
      );
      setEditedOptimizedResume(optimizedResume);
    }
  }, [optimizedResume]);

  // Check if optimized resume is structured data
  useEffect(() => {
    if (optimizedResume && selectedTemplate.type === "premium") {
      try {
        const parsed = JSON.parse(optimizedResume);
        if (parsed.templateId && parsed.header) {
          setOptimizedStructuredData(parsed);
        }
      } catch (e) {
        // Not structured data
        setOptimizedStructuredData(null);
      }
    }
  }, [optimizedResume, selectedTemplate.type]);

  // Clear states when job changes
  useEffect(() => {
    const currentJobId =
      selectedJobForOptimization?.id || selectedJobForOptimization?.job_link;

    if (previousJobId && currentJobId && previousJobId !== currentJobId) {
      console.log(
        "[ResumeOptimizationPage] New job detected, resetting all states"
      );
      // Clear ALL state for the new job
      setEditedOptimizedResume("");
      setOptimizedStructuredData(null);
      setPagesData(null);
      setActionStatus({ state: "idle", message: "" });

      // Force clear the optimized resume if it exists
      if (optimizedResume && setOptimizedResume) {
        setOptimizedResume(null);
      }
    }

    setPreviousJobId(currentJobId);
  }, [
    selectedJobForOptimization,
    previousJobId,
    optimizedResume,
    setOptimizedResume,
  ]);

  const handleOptimizedResumeChange = useCallback((content) => {
    setEditedOptimizedResume(content);
  }, []);

  // Handler for page data changes
  const handlePagesDataChange = useCallback((pages) => {
    setPagesData(pages);
  }, []);

  const handleOptimize = useCallback(async () => {
    // Clear any existing content before starting optimization
    setEditedOptimizedResume("");
    setOptimizedStructuredData(null);

    let contentToOptimize = null;

    if (selectedTemplate.type === "premium" && structuredResumeData) {
      // For premium templates, send the structured data as JSON string
      contentToOptimize = JSON.stringify(structuredResumeData);
      console.log(
        "[ResumeOptimizationPage] Optimizing premium template with structured data"
      );
    } else if (selectedTemplate.type === "basic") {
      // For basic templates, use the HTML content
      contentToOptimize = finalResumeContentForJobSearch;
    }

    if (
      !contentToOptimize &&
      contentToOptimize !== "" &&
      contentToOptimize !== null
    ) {
      alert(
        "Error: No resume content or profile data available for optimization."
      );
      return;
    }

    // If content is empty string or null, we're using profile fallback
    if (contentToOptimize === "" || contentToOptimize === null) {
      console.log(
        "[ResumeOptimizationPage] Using profile data for optimization"
      );
    }

    // Temporarily set the content for optimization
    const originalContent = finalResumeContentForJobSearch;
    if (contentToOptimize !== finalResumeContentForJobSearch) {
      setFinalResumeContentForJobSearch(contentToOptimize);
    }

    try {
      await optimizeResumeAction();
    } finally {
      // Restore original content if needed
      if (contentToOptimize !== originalContent) {
        setFinalResumeContentForJobSearch(originalContent);
      }
    }
  }, [
    optimizeResumeAction,
    finalResumeContentForJobSearch,
    selectedTemplate,
    structuredResumeData,
    setFinalResumeContentForJobSearch,
  ]);

  const handleTemplateTypeChange = (type) => {
    setActiveTemplateType(type);
    if (type === "basic" && basicTemplates.length > 0) {
      setSelectedTemplateId(basicTemplates[0].id);
    } else if (type === "premium" && premiumTemplates.length > 0) {
      setSelectedTemplateId(premiumTemplates[0].id);
    }
    // Auto-collapse after selection
    setIsTemplateSelectorCollapsed(true);
  };

  const handleTemplateSelect = (event) => {
    setSelectedTemplateId(event.target.value);
    // Auto-collapse after selection
    setIsTemplateSelectorCollapsed(true);
  };

  const getStyledHtmlForExport = (content) => {
    const template =
      templates.find((t) => t.id === selectedTemplateId) || templates[0];
    return `<html><head><meta charset="UTF-8"><style>${template.styles}</style></head><body><div class="resume-template-container template-${template.id}">${content}</div></body></html>`;
  };

  const genericDownload = (blob, filename) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const beginAction = (actionName) => {
    setIsActionInProgress(true);
    setActiveAction(actionName);
    setActionStatus({ state: "processing", message: "Please wait..." });
  };

  const endAction = (state = "idle", message = "") => {
    if (state !== "processing") {
      setTimeout(
        () =>
          setActionStatus((prev) =>
            prev.message === message ? { state: "idle", message: "" } : prev
          ),
        3000
      );
    }
    setActionStatus({ state, message });
    setIsActionInProgress(false);
    setActiveAction(null);
  };

  // PDF generation function with pages data support
  const performPdfAction = async (actionType) => {
    const defaultName = `Optimized-${
      selectedJobForOptimization?.jobTitle?.replace(/\s+/g, "_") || "Resume"
    }`;
    const promptMessage =
      actionType === "save"
        ? "Enter name to save optimized PDF resume:"
        : "Enter filename for download:";
    const finalName = window.prompt(promptMessage, defaultName);
    if (!finalName || !finalName.trim()) return;

    beginAction(actionType);

    try {
      setActionStatus({ state: "processing", message: "Generating PDF..." });

      let pdfBlob;

      if (selectedTemplate.type === "premium") {
        // For premium templates, use structured data
        const dataToUse = optimizedStructuredData || structuredResumeData;
        if (!dataToUse) {
          throw new Error("Premium resume data is not available.");
        }

        pdfBlob = await generatePdfBlob(
          null, // No HTML content needed
          selectedTemplate.id,
          dataToUse, // Pass structured data
          null // No pages data for premium templates
        );
      } else {
        // For basic templates, use pages data if available
        const content = editedOptimizedResume || optimizedResume;
        if (!content || content.replace(/<[^>]*>/g, "").trim().length < 30) {
          throw new Error("Cannot process empty or very short content.");
        }

        // Pass both content and pages data
        pdfBlob = await generatePdfBlob(
          content,
          selectedTemplate.id,
          null, // No structured data for basic templates
          pagesData // Pass the pages data for exact layout
        );
      }

      if (actionType === "save") {
        const pdfFile = new File([pdfBlob], `${finalName.trim()}.pdf`, {
          type: "application/pdf",
        });
        const savedResume = await jobService.saveResumeFile(
          pdfFile,
          finalName.trim(),
          false
        );
        incrementResumeListVersion();
        endAction("success", `Resume "${savedResume.title}" saved!`);
      } else {
        genericDownload(pdfBlob, `${finalName.trim()}.pdf`);
        endAction("success", "Download started!");
      }
    } catch (err) {
      console.error("PDF generation error:", err);
      endAction(
        "error",
        err.message || "An error occurred while generating the PDF."
      );
    }
  };

  const handleSaveOptimizedPdf = () => performPdfAction("save");
  const handleDownloadOptimizedPdf = () => performPdfAction("download_pdf");

  // PDF Preview functionality with pages data
  const handlePreviewPdf = async () => {
    try {
      beginAction("preview");
      setActionStatus({
        state: "processing",
        message: "Generating preview...",
      });

      let pdfBlob;

      if (selectedTemplate.type === "premium") {
        const dataToUse = optimizedStructuredData || structuredResumeData;
        if (!dataToUse) {
          throw new Error("Premium resume data is not available.");
        }
        pdfBlob = await generatePdfBlob(
          null,
          selectedTemplate.id,
          dataToUse,
          null
        );
      } else {
        const content = editedOptimizedResume || optimizedResume;
        if (!content || content.replace(/<[^>]*>/g, "").trim().length < 30) {
          throw new Error("Cannot process empty or very short content.");
        }
        // Use pages data for exact layout
        pdfBlob = await generatePdfBlob(
          content,
          selectedTemplate.id,
          null,
          pagesData // Pass the pages data
        );
      }

      // Open PDF in new tab
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");

      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);

      endAction("success", "Preview opened in new tab!");
    } catch (err) {
      console.error("Preview error:", err);
      endAction("error", err.message || "Error generating preview.");
    }
  };

  const performTextDocAction = (fileType) => {
    const suggestedName = `Optimized-${
      selectedJobForOptimization?.jobTitle?.replace(/\s+/g, "_") || "Resume"
    }`;
    const finalName = window.prompt(
      "Enter filename for download:",
      suggestedName
    );
    if (!finalName || !finalName.trim()) return;

    let blob;
    try {
      if (selectedTemplate.type === "premium") {
        const dataToUse = optimizedStructuredData || structuredResumeData;
        if (!dataToUse) throw new Error("Premium data not available.");

        if (fileType === "txt") {
          const textContent = convertStructuredToText(dataToUse);
          blob = new Blob([textContent], { type: "text/plain;charset=utf-8;" });
        } else {
          // This is for the .doc file
          const wordCompatibleHtml = generateSimpleHtmlForWord(dataToUse);
          blob = new Blob(["\ufeff", wordCompatibleHtml], {
            type: "application/msword",
          });
        }
      } else {
        const content = editedOptimizedResume || optimizedResume;
        if (!content || content.replace(/<[^>]*>/g, "").trim().length < 30) {
          throw new Error("Cannot process empty or very short content.");
        }
        if (fileType === "txt") {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = content;
          const textContent = tempDiv.textContent || tempDiv.innerText || "";
          blob = new Blob([textContent], { type: "text/plain;charset=utf-8;" });
        } else {
          const styledHtml = getStyledHtmlForExport(content);
          blob = new Blob(["\ufeff", styledHtml], {
            type: "application/msword",
          });
        }
      }
      genericDownload(
        blob,
        `${finalName.trim()}.${fileType === "txt" ? "txt" : "doc"}`
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownloadOptimizedTxt = () => performTextDocAction("txt");
  const handleDownloadOptimizedDocx = () => performTextDocAction("doc");

  const handleNavigateToCoverLetter = () => {
    navigate("/dashboard/cover-letter");
  };

  const isActionDisabled = () => {
    if (isActionInProgress) return true;
    if (selectedTemplate.type === "premium") {
      return (
        (!structuredResumeData && !optimizedStructuredData) ||
        isLoadingOptimizedResume ||
        isLoadingPremiumResume
      );
    } else {
      const c = editedOptimizedResume || optimizedResume;
      if (isLoadingOptimizedResume || !c || c === "<p></p>") return true;
      return c.replace(/<[^>]*>/g, "").trim().length < 30;
    }
  };

  const renderMainContent = () => {
    if (selectedTemplate.type === "premium") {
      if (
        isLoadingOptimizedResume ||
        isLoadingPremiumResume ||
        isGeneratingPremiumBase
      )
        return (
          <div className="loading-placeholder widget-card p-6 text-center text-gray-600 bg-gray-50 rounded-lg border border-gray-200 animate-pulse">
            {isLoadingOptimizedResume
              ? "Optimizing Premium Resume..."
              : "Generating Premium Resume Structure..."}
          </div>
        );
      if (optimizeResumeError)
        return (
          <div className="widget-card bg-red-50 border border-red-200 p-3 rounded-lg">
            <p className="text-red-700 font-medium">
              Error: {optimizeResumeError}
            </p>
            <button
              onClick={handleOptimize}
              className="smai-button-secondary text-sm mt-2"
            >
              Retry
            </button>
          </div>
        );

      // Use optimized data if available, otherwise use base structured data
      const dataToRender = optimizedStructuredData || structuredResumeData;

      if (dataToRender) {
        return (
          <div
            className="premium-wysiwyg-container"
            style={{
              position: "relative",
              width: "100%",
              height: "calc(100vh - 200px)", // Adjust based on your header/footer
              overflow: "hidden",
            }}
          >
            <ExecutivePremiumTemplate
              data={dataToRender}
              onUpdateData={(newData) => {
                if (optimizedStructuredData) {
                  setOptimizedStructuredData(newData);
                  setOptimizedResume(JSON.stringify(newData));
                } else {
                  setStructuredResumeData(newData);
                }
              }}
            />
          </div>
        );
      }
      return (
        <div className="loading-placeholder widget-card p-6 text-center text-gray-600 bg-gray-50">
          Select a premium template to generate structured content.
        </div>
      );
    } else {
      if (isLoadingOptimizedResume)
        return (
          <div className="loading-placeholder widget-card p-6 text-center text-gray-600 bg-gray-50 rounded-lg border border-gray-200 animate-pulse">
            {finalResumeContentForJobSearch === "" ||
            finalResumeContentForJobSearch === null
              ? "Generating Optimized Resume from Profile..."
              : "Generating Optimized Resume..."}
          </div>
        );
      if (optimizeResumeError)
        return (
          <div className="widget-card bg-red-50 border border-red-200 p-3 rounded-lg">
            <p className="text-red-700 font-medium">
              Error: {optimizeResumeError}
            </p>
            <button
              onClick={handleOptimize}
              className="smai-button-secondary text-sm mt-2"
            >
              Retry
            </button>
          </div>
        );
      return (
        <PdfEditorComponent
          initialContent={editedOptimizedResume || optimizedResume || ""}
          isLoading={isLoadingOptimizedResume}
          onContentChange={handleOptimizedResumeChange}
          onPagesDataChange={handlePagesDataChange}
          templateId={selectedTemplateId}
          key={`${
            selectedJobForOptimization?.id ||
            selectedJobForOptimization?.job_link
          }-${optimizedResume ? "has-content" : "empty"}`}
        />
      );
    }
  };

  if (!isPageReadyForCheck && !aiSessionId) {
    return (
      <div className="p-4 text-center text-gray-500">
        Loading optimization session...
      </div>
    );
  }

  return (
    <div className="resume-optimization-page p-4 md:p-6 lg:p-8 space-y-6 max-w-full mx-auto">
      <div className="text-center pr-8">
        {selectedJobForOptimization && (
          <p className="text-md text-gray-600 mt-1">
            Tailoring your resume for the{" "}
            <strong>{selectedJobForOptimization.jobTitle}</strong> position at{" "}
            <strong>{selectedJobForOptimization.companyName}</strong>.
          </p>
        )}
      </div>

      {!optimizedResume &&
        !isLoadingOptimizedResume &&
        !optimizeResumeError &&
        !editedOptimizedResume &&
        !(
          selectedTemplate.type === "premium" &&
          (isLoadingPremiumResume || isGeneratingPremiumBase)
        ) && (
          <OptimizationControls
            selectedJob={selectedJobForOptimization}
            onOptimize={handleOptimize}
            isLoading={isLoadingOptimizedResume}
            error={optimizeResumeError}
          />
        )}

      {(isLoadingOptimizedResume ||
        optimizedResume ||
        optimizeResumeError ||
        (selectedTemplate.type === "premium" && structuredResumeData) ||
        (selectedTemplate.type === "premium" &&
          (isLoadingPremiumResume || isGeneratingPremiumBase))) && (
        <>
          {/* Collapsible Template Selector */}
          <div
            className="template-selector-wrapper"
            style={{ marginBottom: "1rem" }}
          >
            <button
              onClick={() =>
                setIsTemplateSelectorCollapsed(!isTemplateSelectorCollapsed)
              }
              className="template-selector-toggle"
              style={{
                width: "100%",
                padding: "0.75rem",
                backgroundColor: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: "500",
                color: "#374151",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e5e7eb";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <svg
                  className={`w-4 h-4 transform transition-transform ${
                    isTemplateSelectorCollapsed ? "" : "rotate-90"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                Editor Selection
              </span>

              {/* Optimize for Different Job Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent toggle from firing

                  // Clear all optimization data before navigating
                  console.log(
                    "[ResumeOptimizationPage] Clearing optimization data and navigating back"
                  );

                  // Clear context state
                  if (setOptimizedResume) {
                    setOptimizedResume(null);
                  }
                  if (setStructuredResumeData) {
                    setStructuredResumeData(null);
                  }

                  // Clear local state
                  setEditedOptimizedResume("");
                  setOptimizedStructuredData(null);
                  setPagesData(null);

                  // Navigate back to job listing page
                  navigate("/dashboard/job-listing");
                }}
                style={{
                  padding: "0.375rem 0.75rem",
                  backgroundColor: "#ffffff",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  fontSize: "0.75rem",
                  fontWeight: "500",
                  color: "#4b5563",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                title="Go back to job listings to select a different job"
              >
                Optimize for Different Job
              </button>

              <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                Current:{" "}
                {activeTemplateType === "premium"
                  ? "Premium Editor"
                  : `${selectedTemplate.name} (Basic)`}
              </span>
            </button>

            {!isTemplateSelectorCollapsed && (
              <div
                className="template-selector-content"
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  animation: "slideDown 0.2s ease-out",
                }}
              >
                <div className="template-header-container">
                  <div
                    className={`template-selector-group ${
                      activeTemplateType === "basic" ? "is-active" : ""
                    }`}
                  >
                    <label onClick={() => handleTemplateTypeChange("basic")}>
                      Resume Templates
                    </label>
                    <CSSTransition
                      nodeRef={basicDropdownRef}
                      in={activeTemplateType === "basic"}
                      timeout={300}
                      classNames="dropdown-fluid-fade"
                      unmountOnExit
                    >
                      <select
                        ref={basicDropdownRef}
                        value={selectedTemplateId}
                        onChange={handleTemplateSelect}
                        className="template-dropdown-select"
                      >
                        {basicTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </CSSTransition>
                  </div>
                  <div
                    className={`template-selector-group ${
                      activeTemplateType === "premium" ? "is-active" : ""
                    }`}
                  >
                    <label onClick={() => handleTemplateTypeChange("premium")}>
                      Premium Templates
                    </label>
                    <CSSTransition
                      nodeRef={premiumDropdownRef}
                      in={activeTemplateType === "premium"}
                      timeout={300}
                      classNames="dropdown-fluid-fade"
                      unmountOnExit
                    >
                      <select
                        ref={premiumDropdownRef}
                        value={selectedTemplateId}
                        onChange={handleTemplateSelect}
                        className="template-dropdown-select"
                      >
                        {premiumTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </CSSTransition>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Add CSS for animation */}
          <style jsx>{`
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>

          <div className="widget-card p-0 overflow-hidden relative">
            <TransitionGroup className="content-transition-group">
              <CSSTransition
                nodeRef={contentRef}
                key={selectedTemplate.type}
                timeout={300}
                classNames="content-fade"
              >
                <div className="content-transition-item" ref={contentRef}>
                  {renderMainContent()}
                </div>
              </CSSTransition>
            </TransitionGroup>

            {/* MODIFIED PART: Show buttons only for the basic editor when there's content */}
            {selectedTemplate.type === "basic" && optimizedResume && (
              <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-end gap-3">
                {actionStatus.message && (
                  <span
                    className={`text-sm font-medium order-first sm:order-none w-full sm:w-auto text-center sm:text-right ${
                      actionStatus.state === "success"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {actionStatus.message}
                  </span>
                )}
                <button
                  onClick={handleDownloadOptimizedTxt}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                  title="Download as Plain Text (.txt)"
                >
                  Download TXT
                </button>
                <button
                  onClick={handleDownloadOptimizedDocx}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                  title="Download as Word Document (.doc)"
                >
                  Download DOC
                </button>
                <button
                  onClick={handlePreviewPdf}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                  title="Preview PDF in new tab"
                >
                  Preview PDF
                </button>
                <button
                  onClick={handleDownloadOptimizedPdf}
                  className="opt-page-btn opt-page-btn-secondary"
                  disabled={isActionDisabled()}
                  title="Download Optimized Resume as PDF"
                >
                  Download PDF
                </button>
                <button
                  onClick={handleSaveOptimizedPdf}
                  className="opt-page-btn opt-page-btn-primary"
                  disabled={isActionDisabled()}
                  title="Save Optimized Resume as PDF to Account"
                >
                  Save to Account
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {(optimizedResume ||
        (selectedTemplate.type === "premium" && structuredResumeData)) &&
        !isLoadingOptimizedResume &&
        selectedJobForOptimization && (
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center flex-wrap items-center p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
            <a
              href={selectedJobForOptimization.jobLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 rounded-md text-sm font-medium text-white bg-emerald-800 hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0 flex items-center justify-center no-underline"
            >
              Apply Now
              <svg
                className="h-4 w-4 ml-1.5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
            <button
              onClick={handleNavigateToCoverLetter}
              className="px-6 py-2.5 rounded-md text-sm font-medium border border-emerald-700 text-emerald-800 bg-white hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
            >
              Generate Cover Letter
            </button>
          </div>
        )}
    </div>
  );
}

export default ResumeOptimizationPage;
